// src/pages/Work/UseCases/MobileApp/MobileApp.js
import React from 'react';
import '../DevelopmentTools/DevelopmentTools.css';

function MobileApp({ onNavigate, isFirst, isLast }) {
  return (
    <div className="use-case-container">
      <div className="use-case-header">
        <h3 className="use-case-title">
          Mobile App / Mobile Logistics Applications
        </h3>
        <div className="navigation-buttons">
          <button
            onClick={() => onNavigate('prev')}
            disabled={isFirst}
            className="nav-button"
          >
            &lt;
          </button>
          <button
            onClick={() => onNavigate('next')}
            disabled={isLast}
            className="nav-button"
          >
            &gt;
          </button>
        </div>
      </div>

      {/* Overview */}
      <div id="mobileapp-overview" className="use-case-section">
        <h2 className="section-title">
          Overview 
        </h2>
        <div className="overview-content">
          <p>
            I have redesigned <strong>multiple</strong> logistics <strong>mobile applications</strong>, improving usability, efficiency, and accessibility. By simplifying navigation, <strong>optimizing UI layouts</strong>, and aligning designs with brand guidelines, I streamlined workflows for shipment tracking, inventory management, and reporting.
          </p>

          <p>Key improvements include:</p>
          <ul className="overview-list">
            <li>Bottom navigation & side drawers for intuitive access.</li>
            <li><strong>Scalable UI patterns</strong> to support diverse user roles.</li>
            <li>Improved <strong>touch interactions</strong> & gesture-based navigation.</li>
            <li>Optimized workflows to <strong>reduce cognitive load</strong> and task time.</li>
          </ul>

          <p>
            Through research, prototyping, and testing, I have transformed logistics apps into faster, user-friendly tools, increasing adoption and efficiency.
          </p>
        </div>
      </div>

      {/* Specification */}
      <div id="mobileapp-specification" className="use-case-section">
        <h2 className="section-title">Specification</h2>
        <div className="specification-content">
          <h1 className="tool-name">Adapting Desktop Navigation for Mobile UI</h1>
          <div className="spec-details">
            <div className="spec-row">
              <div className="spec-label">Duration:</div>
              <div>3 months</div>
            </div>
            <div className="spec-row">
              <div className="spec-label">Tools Used:</div>
              <div>Figma, Confluence</div>
            </div>
          </div>
          <div>
            <h3 className="highlight-blue" style={{ fontWeight: 'bold' }}>Goal</h3>
            <p className="goal-text">
              The goal was to create a mobile-friendly default navigation from an existing desktop UI for a complex logistics management application, ensuring seamless usability.
            </p>
          </div>
          {/* Specification Image */}
          <img
            src={require("../../../../assets/use-case-specification.png")}
            alt="Use Case Specification Illustration"
            className="specification-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '2rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      </div>

      {/* Problem Challenge */}
      <div id="mobileapp-problem" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Problem</span> Challenge
        </h2>
        <div className="problem-content">
          <p>I encountered several major UI challenges:</p>
          
          <ul className="overview-list">
            <li>
              <strong>Complex Interfaces:</strong> Many existing solutions had cluttered designs that overwhelmed users.
            </li>
            <li>
              <strong>Limited Screen Space:</strong> Smaller screens constrained the placement of visual elements, requiring a more efficient layout.
            </li>
            <li>
              <strong>Incompatible Navigation:</strong> The desktop menu used a horizontal navigation bar with dropdowns, which was impractical for mobile use.
            </li>
            <li>
              <strong>User Efficiency:</strong> Logistics personnel needed quick, intuitive access to key functions like shipment tracking, inventory management, and reports.
            </li>
            <li>
              <strong>Brand Consistency:</strong> The UI had to align with the company's design system, incorporating its typography, colors, and interaction patterns for a cohesive experience.
            </li>
          </ul>

          <h3 className="highlight-blue" style={{ fontWeight: 'bold', marginTop: '2rem' }}>My impact</h3>
          
          <p>In this project, I focused on:</p>
          
          <ul className="overview-list">
            <li><strong>Enhancing usability</strong> with a mobile-first design that improved task efficiency.</li>
            <li><strong>Reducing cognitive load</strong> by structuring navigation for clarity and discoverability.</li>
            <li><strong>Creating a uniform solution</strong> adaptable across multiple logistics applications.</li>
            <li>Bridging design and development through <strong>close collaboration with engineers</strong>.</li>
            <li><strong>Ensuring brand consistency</strong> by aligning UI elements with company design standards.</li>
            <li>Improving <strong>visual coherence</strong> for better navigation and user retention.</li>
            <li>Tailoring patterns for diverse users, ensuring an intuitive experience for <strong>different roles</strong>.</li>
          </ul>

          <p>
            By implementing these strategies, I delivered a more <strong>intuitive, efficient, and scalable mobile navigation</strong> system that adhered to usability best practices.
          </p>
        </div>
      </div>

      {/* The Process Approach */}
      <div id="mobileapp-process" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>The Process</span> Approach
        </h2>
        <div className="process-content">
          <p>
            In this project, I focused on analyzing the existing system design and common mobile menu standards to ensure an intuitive and efficient navigation experience.
          </p>

          <h3 className="highlight-blue" style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Research & Analysis:</h3>
          <ul className="overview-list">
            <li>Reviewed company <strong>UI guidelines</strong> to ensure <strong>compliance and consistency</strong>.</li>
            <li>Analyzed user behavior through interviews with logistics operators.</li>
            <li>Evaluated common UX patterns for mobile navigation in enterprise applications.</li>
          </ul>

          {/* Card Sorting Image */}
          <h3 className="highlight-blue" style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Information Architecture:</h3>
          <p>I conducted card sorting exercises to optimize the information hierarchy and menu structure.</p>
          <img
            src={require("../../../../assets/card-sorting.png")}
            alt="Card Sorting Process"
            className="card-sorting-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '1rem',
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />

          <h3 className="highlight-blue" style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>User Testing & Iterations:</h3>
          <ul className="overview-list">
            <li><strong>Collected user feedback</strong> to refine menu structure, iconography, and touch interactions.</li>
            <li><strong>Iterated</strong> on readability, spacing, and usability to align with <strong>company standards</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Solution Ideation */}
      <div id="mobileapp-solution" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Solution</span> Ideation
        </h2>
        <div className="solution-content">
          {/* Solution Image */}
          <img
            src={require("../../../../assets/solution-ideation.png")}
            alt="Solution Ideation Illustration"
            className="solution-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginBottom: '1.5rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          
          <p>
            By restructuring the navigation, we ensured that logistics operators could quickly access essential features without compromising usability.
          </p>

          <h3 style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Navigation Bar:</h3>
          <ul className="overview-list">
            <li>Identified <strong>frequently used features</strong> for example: Shipments, Reports, and placed them in a navbar.</li>
            <li>Used <strong>clear, recognizable icons</strong> with the primary color accents for better visibility.</li>
          </ul>

          <h3 style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Side Drawer Menu for Secondary Features:</h3>
          <ul className="overview-list">
            <li>Relocated <strong>advanced settings, profile management, and notifications</strong>.</li>
            <li>Integrated smooth <strong>swipe gestures</strong> for quick and intuitive access.</li>
          </ul>

          <h3 style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Floating Action Button for Quick Actions:</h3>
          <ul className="overview-list">
            <li>Added a prominent FAB for <strong>key tasks</strong>, such as <strong>scanning a QR code</strong>.</li>
          </ul>

          <h3 style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Consistent Typography & Color Scheme:</h3>
          <ul className="overview-list">
            <li>Followed <strong>UI guidelines</strong>, using company font for typography.</li>
            <li>Applied the <strong>primary and secondary color palette</strong> to maintain <strong>brand consistency</strong>.</li>
          </ul>
        </div>
      </div>

      {/* Design Features */}
      <div id="mobileapp-designfeatures" className="use-case-section">
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Design Features</span> Visualization
        </h2>
        <div className="design-features-content">
          <h3 className="highlight-blue" style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>Wireframing & Prototyping:</h3>
          <ul className="overview-list">
            <li><strong>Created low-fidelity wireframes</strong> to explore various mobile navigation structures.</li>
            <li>Developed interactive <strong>prototypes in Figma</strong> to test and optimize user flow efficiency.</li>
          </ul>
          
          {/* Lo-fi wireframes */}
          <h3 style={{ fontWeight: 'bold', marginTop: '2rem' }}>Low-fidelity Wireframes</h3>
          <img
            src={require("../../../../assets/lofi-wireframes.png")}
            alt="Low-fidelity Wireframes"
            className="wireframes-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '1rem',
              marginBottom: '2rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
          
          {/* Hi-fi wireframes */}
          <h3 style={{ fontWeight: 'bold', marginTop: '1.5rem' }}>High-fidelity Wireframes</h3>
          <img
            src={require("../../../../assets/hifi-wireframes.png")}
            alt="High-fidelity Wireframes"
            className="wireframes-image"
            style={{ 
              width: '100%', 
              maxWidth: '600px', 
              marginTop: '1rem',
              marginBottom: '2rem',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      </div>

      {/* Conclusion */}
      <div id="mobileapp-conclusion" className="use-case-section" style={{ marginBottom: '40vh' }}>
        <h2 className="section-title">
          <span className="highlight-blue" style={{ fontWeight: 'bold' }}>Conclusion</span>
        </h2>
        <div className="conclusion-content">
          <p>
            The redesigned mobile navigation significantly improved usability, achieving a <strong>30% faster task completion rate</strong> compared to the previous version. By implementing a <strong>hamburger menu</strong> to handle multiple categories and links, we effectively streamlined access to a broad range of content, making it more manageable in a complex logistics application. The new structure keeps <strong>the main navigation clean and focused on essential tasks</strong>, ensuring efficiency across different user groups. This project highlights my ability to <strong>adhere to branding guidelines</strong>, enhance usability, and <strong>drive meaningful business outcomes</strong> through thoughtful UI/UX design.
          </p>
        </div>
      </div>
    </div>
  );
}

export default MobileApp;